import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ProfileAvatarImage from '@/components/ProfileAvatarImage';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Clock, MessageCircle, Reply, Trash2, 
  Loader2, ChevronDown, ChevronUp, User, Share2,
  PenSquare, EyeOff, Check, Ban, Flag, Bookmark, BookmarkCheck, Shield, FolderEdit, ShieldAlert, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { triggerVerifySparkle } from '@/components/Layout';
import { TierBadge, getTierRingClass } from '@/components/TierBadge';
import { API_URL } from '@/lib/api';
import { setPageSeo } from '@/lib/seo';

const getPremiumLockStyles = (roleName) => {
  switch (roleName) {
    case 'Quiet Mode':
      return {
        cardClass: 'border-blue-400/70 dark:border-blue-500/70 bg-card/95 backdrop-blur-sm',
        iconClass: 'text-blue-500 dark:text-blue-400',
        emphasisClass: 'text-blue-700 dark:text-blue-300',
      };
    case 'Needlessly Rich':
      return {
        cardClass: 'border-amber-400/70 dark:border-amber-500/70 bg-card/95 backdrop-blur-sm',
        iconClass: 'text-amber-500 dark:text-amber-400',
        emphasisClass: 'text-amber-700 dark:text-amber-300',
      };
    case 'After Hours':
    default:
      return {
        cardClass: 'border-purple-400/70 dark:border-purple-500/70 bg-card/95 backdrop-blur-sm',
        iconClass: 'text-purple-500 dark:text-purple-400',
        emphasisClass: 'text-purple-700 dark:text-purple-300',
      };
  }
};
const DEFAULT_AVATAR = '/shh-logo-new.jpeg';
const TAG_SPLIT_REGEX = /(#(?:S\d+|C\d+|A\d+|Poopy\d+))/g;
const TAG_EXACT_REGEX = /^#(?:S\d+|C\d+|A\d+|Poopy\d+)$/;

const getTabForTag = (tag) => {
  if (typeof tag !== 'string') return 'main';
  if (tag.startsWith('#Poopy')) return 'filtered';
  if (tag.startsWith('#A')) return 'archives';
  return 'main';
};

const FEED_RESTORE_INTENT_KEY = 'secretsclub-feed-restore-intent';

const getPublicPostContent = (content) =>
  content === 'filtered to poopy butthole'
    ? 'Moved to the Poopy Butthole section by moderation.'
    : content;

const getAvatarUrl = (pic) => {
  if (!pic) return DEFAULT_AVATAR;
  if (pic.startsWith('/api/')) return `${API_URL}${pic}`;
  return pic;
};

// Share Buttons Component
const ShareButtons = ({ post }) => {
  const postUrl = `${window.location.origin}/post/${post.post_id}`;
  const shareText = `Check out this confession: "${post.tag}" - See what people say about this!`;
  
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground flex items-center gap-1">
        <Share2 className="w-4 h-4" /> Share:
      </span>
      <button
        onClick={shareToFacebook}
        className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
        title="Share on Facebook"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </button>
      <button
        onClick={shareToTwitter}
        className="p-2 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-900/30 text-sky-500"
        title="Share on X (Twitter)"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </button>
    </div>
  );
};

// Clickable Tag Component
const PostTag = ({ tag }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate(`/?tab=${encodeURIComponent(getTabForTag(tag))}&tag=${encodeURIComponent(tag)}`);
  };
  
  return (
    <button
      onClick={handleClick}
      className="font-mono text-sm px-3 py-1 rounded-lg bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400 hover:bg-forest-200 dark:hover:bg-forest-900/50 transition-colors"
    >
      {tag}
    </button>
  );
};

// Render content with clickable tags
const RichContent = ({ text, className = '' }) => {
  const navigate = useNavigate();
  if (!text) return null;
  const parts = text.split(TAG_SPLIT_REGEX);
  return (
    <div className={className}>
      {parts.map((part, i) =>
        TAG_EXACT_REGEX.test(part) ? (
          <button
            key={i}
            onClick={() => navigate(`/?tab=${encodeURIComponent(getTabForTag(part))}&tag=${encodeURIComponent(part)}`)}
            className="font-mono text-forest-700 dark:text-forest-400 hover:underline cursor-pointer"
            data-testid={`inline-tag-${part}`}
          >
            {part}
          </button>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </div>
  );
};

// Emoji Reactions Component
const EmojiReactions = ({ reactions, onReact, userRole, allowedEmojis = [], isViewerUnverified = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  
  // Close picker on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false);
    };
    if (showPicker) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPicker]);

  const handleReact = (emoji) => {
    if (isViewerUnverified) {
      triggerVerifySparkle();
      toast.error('Verify your email to react to comments');
      return;
    }
    onReact(emoji);
  };

  const handlePickerToggle = () => {
    if (isViewerUnverified) {
      triggerVerifySparkle();
      toast.error('Verify your email to react to comments');
      return;
    }
    setShowPicker(!showPicker);
  };

  return (
    <div className="flex flex-wrap items-center gap-1 mt-2">
      {Object.entries(reactions || {}).map(([emoji, users]) => (
        users.length > 0 && (
          <button
            key={emoji}
            onClick={() => handleReact(emoji)}
            className="flex items-center gap-1 text-sm bg-muted/50 rounded-full px-2 py-0.5 hover:bg-muted"
          >
            <span>{emoji}</span>
            <span className="text-xs text-muted-foreground">{users.length}</span>
          </button>
        )
      ))}
      <div className="relative" ref={pickerRef}>
        <button
          onClick={handlePickerToggle}
          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
          data-testid="emoji-picker-toggle"
        >
          +
        </button>
        {showPicker && (
          <div className="absolute bottom-full left-0 mb-2 p-2.5 bg-card border rounded-xl shadow-lg z-50 flex flex-wrap gap-1.5" style={{ width: 'max-content', maxWidth: '360px' }} data-testid="emoji-picker-dropdown">
            {allowedEmojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => { handleReact(emoji); setShowPicker(false); }}
                className="text-xl px-2 py-1.5 rounded hover:bg-muted whitespace-nowrap"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Single Comment Component
const CommentItem = ({ comment, onDelete, onReact, onReply, onRefresh, onBlock, onReport, canReport, canViewRealNames, user, isReply = false, allowedEmojis = [], isViewerUnverified = false }) => {
  const [replyMode, setReplyMode] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const isOwner = user?.user_id === comment.user_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canDelete = isOwner || isAdmin;
  const canEdit = isOwner;
  const canHide = isAdmin && !comment.is_hidden;
  
  const formattedDate = comment.created_at 
    ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })
    : 'Recently';

  const handleReply = async () => {
    if (!replyContent.trim()) return;
    setSubmitting(true);
    try {
      await onReply(replyContent, comment.comment_id);
      setReplyContent('');
      setReplyMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/comments/${comment.comment_id}`,
        { content: editContent }
      );
      toast.success(comment.is_hidden ? 'Comment updated and sent for approval' : 'Comment updated');
      setEditing(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit comment');
    } finally {
      setSaving(false);
    }
  };

  const handleHide = async () => {
    try {
      await axios.put(
        `${API_URL}/api/comments/${comment.comment_id}/hide`
      );
      toast.success('Comment hidden');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to hide comment');
    }
  };

  const handleApprove = async () => {
    try {
      await axios.put(
        `${API_URL}/api/comments/${comment.comment_id}/approve`
      );
      toast.success('Comment approved');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to approve comment');
    }
  };

  const handleViewHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/comments/${comment.comment_id}/history`
      );
      setEditHistory(response.data.edit_history || []);
      setShowHistory(true);
    } catch (error) {
      toast.error('Cannot view edit history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUnhide = async () => {
    try {
      await axios.put(
        `${API_URL}/api/comments/${comment.comment_id}/unhide`
      );
      toast.success('Comment unhidden');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to unhide comment');
    }
  };

  // Hidden comment view
  if (comment.is_hidden && !isAdmin && !isOwner) {
    return (
      <div className={`py-4 ${isReply ? 'ml-8 border-l-2 border-border pl-4' : ''}`}>
        <p className="text-sm text-muted-foreground italic" data-testid="hidden-comment">Hidden by admin</p>
      </div>
    );
  }

  return (
    <div className={`py-4 ${isReply ? 'ml-8 border-l-2 border-border pl-4' : ''}`} data-testid={`comment-${comment.comment_id}`}>
      <div className="flex items-start gap-3">
        <Avatar className="w-8 h-8">
          {comment.is_anonymous ? (
            <AvatarFallback className="bg-forest-100 dark:bg-forest-900/30 text-forest-700">?</AvatarFallback>
          ) : (
            <>
              <ProfileAvatarImage
                src={getAvatarUrl(comment.profile_picture)}
                zoom={comment.profile_picture_zoom}
                positionX={comment.profile_picture_position_x}
                positionY={comment.profile_picture_position_y}
              />
              <AvatarFallback>
                {(comment.display_name || '?')[0]?.toUpperCase()}
              </AvatarFallback>
            </>
          )}
        </Avatar>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {comment.is_anonymous ? (
              <span className="font-mono text-xs bg-forest-100 dark:bg-forest-900/30 px-2 py-1 rounded">
                Anonymous User {comment.anonymous_id}
              </span>
            ) : isViewerUnverified ? (
              <span className="text-sm text-muted-foreground italic">Member</span>
            ) : (
              <>
                {comment.user_id ? (
                  <Link 
                    to={`/profile/${comment.user_id}`}
                    className="font-medium hover:text-forest-600"
                  >
                    {comment.display_name}
                  </Link>
                ) : (
                  <span className="font-medium">{comment.display_name || 'Unknown'}</span>
                )}
                {/* Confidant: show real name in parentheses */}
                {comment.is_confidant && comment.real_name && (
                  <span className="text-xs text-muted-foreground">({comment.real_name})</span>
                )}
                {/* Admin: show real name + account name with shield */}
                {!comment.is_confidant && isAdmin && comment.real_name && (
                  <span className="text-xs text-muted-foreground">({comment.real_name})</span>
                )}
                {isAdmin && comment.account_name && (
                  <span className="text-xs text-muted-foreground/60 flex items-center gap-0.5">
                    <Shield className="w-2.5 h-2.5" />[{comment.account_name}]
                  </span>
                )}
                {/* Role-based real name (not admin, not confidant but has permission) */}
                {!comment.is_confidant && !isAdmin && comment.real_name && canViewRealNames && (
                  <span className="text-xs text-muted-foreground">({comment.real_name})</span>
                )}
                {comment.user_tags?.length > 0 && comment.user_tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] py-0 px-1.5 h-5 border-forest-300 dark:border-forest-700 text-forest-700 dark:text-forest-400">
                    {tag.tag_name}
                  </Badge>
                ))}
              </>
            )}
            {/* Admin: show real identity for anonymous comments */}
            {isAdmin && comment.is_anonymous && comment.real_username && (
              <Link 
                to={`/profile/${comment.user_id}`}
                className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
                title="Real identity (admin only)"
                data-testid="admin-real-identity"
              >
                ({comment.real_username})
              </Link>
            )}
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formattedDate}
            </span>
            {comment.is_edited && (
              isAdmin ? (
                <button
                  onClick={handleViewHistory}
                  className="text-xs text-muted-foreground hover:text-forest-600 underline cursor-pointer"
                  data-testid="edit-history-link"
                >
                  {loadingHistory ? 'Loading...' : 'Edited'}
                </button>
              ) : (
                <span className="text-xs text-muted-foreground italic">Edited</span>
              )
            )}
            {comment.is_hidden && (
              <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">Hidden</Badge>
            )}
            {comment.pending_approval && (
              <Badge variant="outline" className="text-xs border-blue-500 text-blue-600">Pending Approval</Badge>
            )}
          </div>
          
          {/* Content or edit form */}
          {editing ? (
            <div className="mt-2 space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[80px]"
                data-testid="edit-comment-input"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleEdit} disabled={saving || !editContent.trim()}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setEditContent(comment.content); }}>Cancel</Button>
              </div>
            </div>
          ) : comment.is_hidden && isOwner ? (
            <div className="mt-1">
              <p className="text-sm text-amber-600 dark:text-amber-400 italic mb-2">
                This comment was hidden by an admin. Edit it to submit for re-approval.
              </p>
              <p className="leading-relaxed line-through text-muted-foreground">{comment.content}</p>
              <button
                onClick={() => setEditing(true)}
                className="text-xs text-forest-600 hover:underline mt-1"
                data-testid="re-edit-hidden-btn"
              >
                Edit and resubmit
              </button>
            </div>
          ) : (
            <RichContent text={comment.content} className="mt-1 leading-relaxed whitespace-pre-wrap" />
          )}
          
          {!editing && !comment.is_hidden && (
            <EmojiReactions 
              reactions={comment.reactions}
              onReact={(emoji) => onReact(comment.comment_id, emoji)}
              userRole={user?.role}
              allowedEmojis={allowedEmojis}
              isViewerUnverified={isViewerUnverified}
            />
          )}
          
          <div className="flex items-center gap-3 mt-2">
            {user && !isReply && !comment.is_hidden && !isViewerUnverified && (
              <button
                onClick={() => setReplyMode(!replyMode)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Reply className="w-3 h-3" /> Reply
              </button>
            )}
            {user && !isReply && !comment.is_hidden && isViewerUnverified && (
              <button
                onClick={() => { triggerVerifySparkle(); toast.error('Verify your email to reply'); }}
                className="text-xs text-muted-foreground/50 flex items-center gap-1"
              >
                <Reply className="w-3 h-3" /> Reply
              </button>
            )}
            {canEdit && !comment.is_hidden && (
              <button
                onClick={() => setEditing(true)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="edit-comment-btn"
              >
                <PenSquare className="w-3 h-3" /> Edit
              </button>
            )}
            {canHide && (
              <button
                onClick={handleHide}
                className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
                data-testid="hide-comment-btn"
              >
                <EyeOff className="w-3 h-3" /> Hide
              </button>
            )}
            {isAdmin && comment.is_hidden && (
              <button
                onClick={handleUnhide}
                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                data-testid="unhide-comment-btn"
              >
                <Check className="w-3 h-3" /> Unhide
              </button>
            )}
            {isAdmin && comment.pending_approval && (
              <button
                onClick={handleApprove}
                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                data-testid="approve-comment-btn"
              >
                <Check className="w-3 h-3" /> Approve
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => onDelete(comment.comment_id)}
                className="text-xs text-destructive/70 hover:text-destructive flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
            {user && !isOwner && !comment.is_anonymous && onBlock && (
              <button
                onClick={() => onBlock(comment.user_id, comment.display_name)}
                className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                data-testid={`block-commenter-${comment.comment_id}`}
              >
                <Ban className="w-3 h-3" /> Block
              </button>
            )}
            {canReport && !isOwner && onReport && (
              <button
                onClick={() => onReport(comment.comment_id)}
                className="text-xs text-amber-500 hover:text-amber-700 flex items-center gap-1"
                data-testid={`report-comment-${comment.comment_id}`}
              >
                <Flag className="w-3 h-3" /> Report
              </button>
            )}
          </div>

          {replyMode && (
            <div className="mt-3 space-y-2">
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                className="min-h-[80px]"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReply} disabled={submitting || !replyContent.trim()}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reply'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setReplyMode(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit History Dialog */}
      {showHistory && (
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent className="sm:max-w-md max-h-[70vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit History</DialogTitle>
              <DialogDescription>Previous versions of this comment</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="p-3 bg-muted/50 rounded-lg border-l-4 border-forest-600">
                <p className="text-xs text-muted-foreground mb-1 font-medium">Current version</p>
                <p className="text-sm">{comment.content}</p>
              </div>
              {editHistory.length > 0 ? (
                editHistory.slice().reverse().map((entry, i) => (
                  <div key={i} className="p-3 bg-muted/30 rounded-lg border-l-4 border-muted-foreground/30">
                    <p className="text-xs text-muted-foreground mb-1">
                      {entry.edited_at ? formatDistanceToNow(new Date(entry.edited_at), { addSuffix: true }) : 'Unknown time'}
                    </p>
                    <p className="text-sm text-muted-foreground">{entry.content}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground italic">No previous versions</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Comment Form Component
const CommentForm = ({ onSubmit, userIdentity, user, isViewerUnverified = false }) => {
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isLocked = userIdentity !== null;

  useEffect(() => {
    if (isLocked) {
      setIsAnonymous(userIdentity?.is_anonymous === true);
    }
  }, [isLocked, userIdentity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    setSubmitting(true);
    try {
      await onSubmit(content, isAnonymous);
      setContent('');
      toast.success('Comment posted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Card className="bg-muted/30">
        <CardContent className="py-6 text-center">
          <User className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground mb-4">Sign in to join the conversation</p>
          <Link to="/login">
            <Button className="bg-forest-800 hover:bg-forest-900 text-white">Sign In</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (isViewerUnverified) {
    return (
      <Card className="bg-amber-50/50 dark:bg-amber-900/10 border-amber-500/30">
        <CardContent className="py-6 text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-amber-500/70 mb-3" />
          <p className="text-amber-800 dark:text-amber-300 font-medium mb-1">Verify your email to comment</p>
          <p className="text-sm text-amber-700/70 dark:text-amber-400/70 mb-4">
            Check your inbox and reply to our verification email to unlock commenting.
          </p>
          <Button
            variant="outline"
            className="border-amber-500/50 text-amber-700 dark:text-amber-300"
            onClick={() => { triggerVerifySparkle(); }}
            data-testid="verify-to-comment-btn"
          >
            <ShieldAlert className="w-4 h-4 mr-2" />
            Verify Account
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="w-10 h-10">
              <ProfileAvatarImage
                src={getAvatarUrl(user.profile_picture)}
                alt={user.display_name || user.username}
                zoom={user.profile_picture_zoom}
                positionX={user.profile_picture_position_x}
                positionY={user.profile_picture_position_y}
              />
              <AvatarFallback>
                {(user.display_name || user.username || '?')[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{isAnonymous ? 'Commenting anonymously' : (user.display_name || user.username)}</p>
              <p className="text-xs text-muted-foreground">
                {isLocked 
                  ? `Your identity is locked to ${userIdentity?.is_anonymous ? 'anonymous' : 'your display name'} for this post`
                  : 'Choose your identity for this post'
                }
              </p>
            </div>
          </div>

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share your thoughts..."
            className="min-h-[100px]"
            data-testid="comment-textarea"
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="anonymous"
                checked={isAnonymous}
                onCheckedChange={setIsAnonymous}
                disabled={isLocked}
              />
              <Label htmlFor="anonymous" className={`text-sm ${isLocked ? 'text-muted-foreground' : ''}`}>
                Comment anonymously
              </Label>
            </div>

            <Button
              type="submit"
              disabled={submitting || !content.trim()}
              className="bg-forest-800 hover:bg-forest-900 text-white"
              data-testid="submit-comment-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Comment'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Main Post Page
export default function PostPage() {
  const { postId } = useParams();
  const { user, canReport } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [lightboxImage, setLightboxImage] = useState(null);
  const isViewerUnverified = user && !user.email_verified && !user.oauth_provider;
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userIdentity, setUserIdentity] = useState(null);
  const [allowedEmojis, setAllowedEmojis] = useState(['👍', '❤️', '😂', '😮', '😢', '😡']);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportCommentId, setReportCommentId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [canViewRealNames, setCanViewRealNames] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicPopoverOpen, setTopicPopoverOpen] = useState(false);
  const [changingTopic, setChangingTopic] = useState(false);
  const [showNewTopicInput, setShowNewTopicInput] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const backToFeedHref = `${location.state?.fromFeed?.pathname || '/'}${location.state?.fromFeed?.search || ''}`;
  const hasFeedHistory = Boolean(location.state?.fromFeed);

  const handleBackToFeed = () => {
    if (backToFeedHref) {
      sessionStorage.setItem(FEED_RESTORE_INTENT_KEY, backToFeedHref);
    }
    if (hasFeedHistory) {
      navigate(-1);
    } else {
      navigate(backToFeedHref);
    }
  };

  useEffect(() => {
    setPageSeo({
      title: post ? `${post.tag || post.title || 'Confession'} - Anonymous Confession` : 'Anonymous Confession',
      description: post?.content
        ? getPublicPostContent(post.content).replace(/\s+/g, ' ').slice(0, 155)
        : 'Read and discuss an anonymous confession on Secrets Club.',
      path: `/post/${postId}`,
      type: 'article',
    });
  }, [post, postId]);

  const fetchPost = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      toast.error('Post not found');
      navigate('/');
    }
  }, [navigate, postId]);

  const fetchComments = React.useCallback(async () => {
    try {
      
      const response = await axios.get(`${API_URL}/api/posts/${postId}/comments`);
      setComments(response.data.comments);
      
      // Also fetch allowed emojis from response if available
      if (response.data.allowed_emojis) {
        setAllowedEmojis(response.data.allowed_emojis);
      }
      
      if (user) {
        const findUserComment = (commentList) => {
          for (const comment of commentList) {
            if (comment.user_id === user.user_id) {
              return { is_anonymous: comment.is_anonymous, anonymous_id: comment.anonymous_id };
            }
            if (comment.replies) {
              const found = findUserComment(comment.replies);
              if (found) return found;
            }
          }
          return null;
        };
        setUserIdentity(findUserComment(response.data.comments));
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  }, [postId, user]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchPost();
      await fetchComments();
      setLoading(false);
    };
    loadData();
    // Fetch follow status if logged in
    if (user && postId) {
      axios.get(`${API_URL}/api/posts/${postId}/follow-status`).then(res => setIsFollowing(res.data.is_following)).catch(() => {});
    }
    // Check if user can view real names (admin always can, or role-based permission)
    if (user) {
      if (isAdmin) {
        setCanViewRealNames(true);
      } else if (user) {
        axios.get(`${API_URL}/api/admin/my-permissions`).then(res => {
          setCanViewRealNames(res.data.permissions?.includes('view_real_names') || false);
        }).catch(() => {});
      }
    }
    // Fetch topics for admin topic change
    if (isAdmin) {
      axios.get(`${API_URL}/api/topics`).then(res => setTopics(res.data.topics || [])).catch(() => {});
    }
  }, [fetchComments, fetchPost, isAdmin, postId, user]);

  const handleChangeTopic = async (topicId) => {
    setChangingTopic(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/posts/${postId}/topic`,
        { topic_id: topicId || null },
        
      );
      setPost(p => ({ ...p, topic_id: res.data.topic_id, topic_name: res.data.topic_name }));
      setTopicPopoverOpen(false);
      toast.success('Topic updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update topic');
    } finally {
      setChangingTopic(false);
    }
  };

  const handleCreateTopicInline = async () => {
    if (!newTopicName.trim()) return;
    setChangingTopic(true);
    try {
      const res = await axios.post(`${API_URL}/api/topics`, { name: newTopicName.trim() });
      const created = res.data;
      setTopics(prev => [...prev, created]);
      await handleChangeTopic(created.topic_id);
      setNewTopicName('');
      setShowNewTopicInput(false);
      toast.success(`Topic "${created.name}" created!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create topic');
      setChangingTopic(false);
    }
  };

  const handleCommentSubmit = async (content, isAnonymous, parentCommentId = null) => {
    const response = await axios.post(
      `${API_URL}/api/posts/${postId}/comments`,
      { content, is_anonymous: isAnonymous, parent_comment_id: parentCommentId },
      
    );
    
    await fetchComments();

    if (response.data?.pending_approval) {
      toast.success('Comment submitted for moderator approval.');
    } else {
      toast.success('Comment posted.');
    }
    
    if (!userIdentity) {
      setUserIdentity({ is_anonymous: isAnonymous, anonymous_id: response.data.anonymous_id });
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/comments/${commentId}`);
      toast.success('Comment deleted');
      await fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleReaction = async (commentId, emoji) => {
    if (!user) {
      toast.error('Please sign in to react');
      return;
    }
    if (isViewerUnverified) {
      triggerVerifySparkle();
      toast.error('Verify your email to react');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/comments/${commentId}/reactions`,
        { emoji },
        
      );
      await fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add reaction');
    }
  };

  const handlePostReaction = async (emoji) => {
    if (!user) return;
    if (isViewerUnverified) {
      triggerVerifySparkle();
      toast.error('Verify your email to react');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/reactions`, { emoji });
      setPost(prev => ({ ...prev, reactions: res.data.reactions }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to react');
    }
  };

  const handleToggleAdEligibility = async () => {
    if (!post) return;
    const nextAdEligible = post.ad_eligible === false;
    try {
      const res = await axios.patch(`${API_URL}/api/posts/${postId}/ad-eligibility`, {
        ad_eligible: nextAdEligible,
      });
      setPost(prev => ({
        ...prev,
        ad_eligible: res.data.ad_eligible,
        ad_ineligible: res.data.ad_ineligible,
        ad_ineligible_reason: res.data.ad_ineligible_reason,
      }));
      toast.success(nextAdEligible ? 'Post marked ad eligible' : 'Post marked ad ineligible');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ad eligibility');
    }
  };

  const handleBlockFromComment = (userId, displayName) => {
    setBlockTarget({ userId, displayName });
    setShowBlockDialog(true);
  };

  const confirmBlock = async () => {
    if (!blockTarget) return;
    try {
      await axios.post(
        `${API_URL}/api/users/${blockTarget.userId}/block`,
        
      );
      toast.success(`${blockTarget.displayName} blocked. Their comments are now hidden.`);
      setShowBlockDialog(false);
      setBlockTarget(null);
      await fetchComments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleReportComment = (commentId) => {
    setReportCommentId(commentId);
    setReportReason('');
    setShowReportDialog(true);
  };

  const confirmReport = async () => {
    if (!reportCommentId) return;
    try {
      await axios.post(
        `${API_URL}/api/comments/${reportCommentId}/report`,
        { reason: reportReason || null },
        
      );
      toast.success('Comment reported. An admin will review it.');
      setShowReportDialog(false);
      setReportCommentId(null);
      setReportReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report comment');
    }
  };

  const toggleFollow = async () => {
    if (!user) {
      toast.error('Please sign in to follow posts');
      return;
    }
    if (isViewerUnverified) {
      triggerVerifySparkle();
      toast.error('Verify your email to follow posts');
      return;
    }
    try {
      if (isFollowing) {
        await axios.delete(`${API_URL}/api/posts/${postId}/follow`);
        setIsFollowing(false);
        toast.success('Unfollowed post');
      } else {
        await axios.post(`${API_URL}/api/posts/${postId}/follow`);
        setIsFollowing(true);
        toast.success('Following post - you\'ll get notified of new comments');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update follow');
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Skeleton className="h-8 w-32 mb-8" />
        <Skeleton className="h-64 w-full rounded-2xl mb-4" />
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!post) return null;

  if (post.locked_content && post.locked_reason === 'premium') {
    const membershipPath =
      post.locked_cta_url ||
      `/membership/${post.premium_role === 'After Hours' ? 'after-hours' : post.premium_role === 'Needlessly Rich' ? 'needlessly-rich' : 'quiet-mode'}`;
    const premiumLabel = post.is_filtered ? 'Poopy Butthole Premium Post' : (post.tag || 'Premium Post');
    const premiumLockStyles = getPremiumLockStyles(post.premium_role);

    return (
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="post-page-premium-locked">
        <button
          type="button"
          onClick={handleBackToFeed}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Feed
        </button>
        <Card className={`overflow-hidden ${premiumLockStyles.cardClass}`}>
          <CardContent className="py-10 text-center">
            <ShieldAlert className={`w-12 h-12 mx-auto mb-4 ${premiumLockStyles.iconClass}`} />
            <h3 className="font-serif text-2xl font-semibold mb-2">{premiumLabel}</h3>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-4">
              This post stays on the server until we confirm the account has the required premium membership.
            </p>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-6">
              Unlock this post with <span className={`font-medium ${premiumLockStyles.emphasisClass}`}>{post.premium_role}</span> membership or higher.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {user ? (
                <Link to={membershipPath} data-testid="premium-cta-membership-post">
                  <Button className="btn-primary px-6">Upgrade to Unlock</Button>
                </Link>
              ) : (
                <Link to="/register" data-testid="premium-cta-register-post">
                  <Button className="btn-primary px-6">Join to Unlock</Button>
                </Link>
              )}
            </div>
            {post.topic_name && (
              <Badge variant="outline" className="mt-4">{post.topic_name}</Badge>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Adblock-locked placeholder from server
  if (post.locked_content) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="post-page-locked">
        <button
          type="button"
          onClick={handleBackToFeed}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Feed
        </button>
        <Card className="overflow-hidden border-amber-200 dark:border-amber-800/60 bg-gradient-to-br from-amber-50/50 to-background dark:from-amber-950/10">
          <CardContent className="py-10 text-center">
            <Shield className="w-12 h-12 mx-auto text-amber-500 dark:text-amber-400 mb-4" />
            <h3 className="font-serif text-xl font-semibold mb-3">Ad Blocker Detected</h3>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-4">
              It appears that you're using an ad blocker. Secrets Club is free because of the ads we show.
            </p>
            <p className="text-muted-foreground leading-relaxed max-w-md mx-auto mb-6">
              If you'd like to see the real secrets, you can either turn off your ad blocker and refresh the page, or become a member.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/membership" data-testid="adblock-cta-membership-post">
                <Button className="btn-primary px-6">Become a Quiet Mode Member</Button>
              </Link>
              <Button variant="outline" onClick={() => window.location.reload()} data-testid="adblock-cta-refresh-post">
                I've Disabled My Ad Blocker
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formattedDate = post.created_at 
    ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true })
    : 'Recently';

  // Flatten comments for simple rendering
  const flattenedComments = [];
  const processComments = (commentList, isReply = false) => {
    commentList.forEach(comment => {
      flattenedComments.push({ ...comment, isReply });
      if (comment.replies && comment.replies.length > 0) {
        processComments(comment.replies, true);
      }
    });
  };
  processComments(comments);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="post-page">
      <button
        type="button"
        onClick={handleBackToFeed}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Feed
      </button>

      <article className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Avatar className={`w-8 h-8 ${getTierRingClass(post.author_premium_tier)}`}>
              {post.author_profile_picture && (
                <ProfileAvatarImage
                  src={post.author_profile_picture}
                  alt={post.author_username}
                  zoom={post.author_profile_picture_zoom}
                  positionX={post.author_profile_picture_position_x}
                  positionY={post.author_profile_picture_position_y}
                />
              )}
              <AvatarFallback className="bg-forest-100 text-forest-700">{(post.author_username || 'A')[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="font-medium text-foreground">{post.author_username}</span>
            {post.author_premium_tier > 0 && (
              <TierBadge tier={post.author_premium_tier} roleName={post.author_premium_role_name} />
            )}
            <span>·</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formattedDate}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <Button 
                variant={isFollowing ? "default" : "outline"} 
                size="sm"
                onClick={toggleFollow}
                className={isFollowing ? "bg-forest-600 hover:bg-forest-700 text-white" : ""}
                data-testid="follow-post-btn"
              >
                {isFollowing ? <BookmarkCheck className="w-4 h-4 mr-1" /> : <Bookmark className="w-4 h-4 mr-1" />}
                {isFollowing ? 'Following' : 'Follow'}
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleAdEligibility}
                className={post.ad_eligible === false ? 'text-red-600 border-red-300' : 'text-emerald-700 border-emerald-300'}
                data-testid="post-ad-eligibility-toggle"
              >
                {post.ad_eligible === false ? 'No Ads' : 'Ads OK'}
              </Button>
            )}
            <ShareButtons post={post} />
          </div>
        </div>

        {/* Tag and Topic */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {isAdmin ? (
            <Popover open={topicPopoverOpen} onOpenChange={setTopicPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors hover:bg-muted cursor-pointer"
                  data-testid="topic-change-btn"
                >
                  {post.topic_name || 'No Topic'}
                  <FolderEdit className="w-3 h-3 ml-0.5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  {showNewTopicInput ? (
                    <div className="flex items-center gap-1 mb-1">
                      <Input
                        value={newTopicName}
                        onChange={(e) => setNewTopicName(e.target.value)}
                        placeholder="Topic name..."
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateTopicInline()}
                        className="h-7 text-sm"
                        data-testid="postpage-new-topic-input"
                        autoFocus
                      />
                      <Button size="sm" className="h-7 px-2" onClick={handleCreateTopicInline} disabled={changingTopic || !newTopicName.trim()}>
                        Add
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => { setShowNewTopicInput(false); setNewTopicName(''); }}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted text-forest-700 dark:text-forest-400 font-medium"
                      onClick={() => setShowNewTopicInput(true)}
                      data-testid="postpage-create-topic-btn"
                    >
                      + Create New Topic
                    </button>
                  )}
                  <button
                    className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted ${!post.topic_id ? 'font-semibold bg-muted/50' : ''}`}
                    onClick={() => handleChangeTopic(null)}
                    disabled={changingTopic}
                  >
                    No Topic
                  </button>
                  {topics.map(t => (
                    <button
                      key={t.topic_id}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-muted ${post.topic_id === t.topic_id ? 'font-semibold bg-muted/50' : ''}`}
                      onClick={() => handleChangeTopic(t.topic_id)}
                      disabled={changingTopic}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          ) : post.topic_name ? (
            <Badge variant="outline">{post.topic_name}</Badge>
          ) : null}
          {post.is_archived && (
            <Badge variant="outline" className="border-sky-400/50 text-sky-700 dark:text-sky-300">
              Archives
            </Badge>
          )}
        </div>

        <h1 className="font-mono text-3xl md:text-4xl font-bold mb-6">{post.tag}</h1>

        {/* Image - below tag, above text, indented and smaller */}
        {post.image_url && (
          <div className="mx-4 sm:mx-8 mb-6">
            <img
              src={post.image_url}
              alt={post.title}
              className="max-w-full max-h-96 rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setLightboxImage(post.image_url)}
              data-testid="post-detail-image"
            />
          </div>
        )}

        <RichContent 
          text={getPublicPostContent(post.content)}
          className="bg-muted/50 rounded-xl p-6 border-l-4 border-forest-600 italic font-serif text-lg whitespace-pre-wrap"
        />

        {/* Post Reactions */}
        <div className="flex flex-wrap items-center gap-1.5 mt-4">
          {Object.entries(post.reactions || {}).map(([emoji, users]) => (
            users.length > 0 && (
              <button
                key={emoji}
                onClick={() => handlePostReaction(emoji)}
                className={`flex items-center gap-1 text-sm rounded-full px-2.5 py-1 transition-colors ${
                  user && users.includes(user.user_id)
                    ? 'bg-forest-100 dark:bg-forest-900/40 border border-forest-300 dark:border-forest-700'
                    : 'bg-muted/50 hover:bg-muted'
                }`}
                data-testid={`post-detail-reaction-${emoji}`}
              >
                <span>{emoji}</span>
                <span className="text-xs text-muted-foreground">{users.length}</span>
              </button>
            )
          ))}
          {user && (
            <EmojiReactions
              reactions={{}}
              onReact={handlePostReaction}
              userRole={user?.role}
              allowedEmojis={allowedEmojis}
              isViewerUnverified={isViewerUnverified}
            />
          )}
        </div>

        {/* Mentioned tags */}
        {post.mentioned_tags && post.mentioned_tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
            <span className="text-sm text-muted-foreground">References:</span>
            {post.mentioned_tags.map(tag => (
              <PostTag key={tag} tag={tag} />
            ))}
          </div>
        )}
      </article>

      {user?.premium_tier === 0 && (
        <div className="border border-dashed border-muted-foreground/30 rounded-xl p-4 text-center text-muted-foreground text-sm mb-10">
          <p className="text-xs uppercase tracking-wider mb-2">Advertisement</p>
          <div className="h-24 bg-muted/30 rounded-lg flex items-center justify-center">
            <span className="opacity-50">Google Ad Space</span>
          </div>
        </div>
      )}

      <section>
        <h2 className="font-serif text-2xl font-semibold mb-6 flex items-center gap-2">
          <MessageCircle className="w-6 h-6" />
          Comments ({comments.length})
        </h2>

        <div className="mb-8">
          <CommentForm 
            onSubmit={handleCommentSubmit}
            userIdentity={userIdentity}
            user={user}
            isViewerUnverified={isViewerUnverified}
          />
        </div>

        <div className="divide-y divide-border">
          {flattenedComments.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No comments yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            flattenedComments.map(comment => (
              <CommentItem
                key={comment.comment_id}
                comment={comment}
                onDelete={handleDeleteComment}
                onReact={handleReaction}
                onReply={(content, parentId) => handleCommentSubmit(content, userIdentity?.is_anonymous || false, parentId)}
                onRefresh={fetchComments}
                onBlock={handleBlockFromComment}
                onReport={handleReportComment}
                canReport={canReport}
                canViewRealNames={canViewRealNames}
                user={user}
                isReply={comment.isReply}
                allowedEmojis={allowedEmojis}
                isViewerUnverified={isViewerUnverified}
              />
            ))
          )}
        </div>
      </section>

      {/* Block Confirmation Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Block {blockTarget?.displayName}?</DialogTitle>
            <DialogDescription>
              This is permanent and cannot be undone by you. You will no longer see their comments, and they won't see yours.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmBlock} data-testid="confirm-block-from-comment">
              <Ban className="w-4 h-4 mr-1" /> Block User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Confirmation Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Report Comment</DialogTitle>
            <DialogDescription>
              This comment will be reviewed by a moderator.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium mb-1 block">Reason (optional)</Label>
            <Textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Why are you reporting this comment?"
              rows={3}
              data-testid="report-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={confirmReport} data-testid="confirm-report-btn">
              <Flag className="w-4 h-4 mr-1" /> Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 z-10 transition-colors"
            onClick={() => setLightboxImage(null)}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
